import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 980,
    margin: "0 auto",
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  shell: {
    borderRadius: 24,
    padding: theme.spacing(4),
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      borderRadius: 18,
    },
  },
  heading: {
    textAlign: "center",
    color: "#0f3b62",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.8rem",
    },
  },
  intro: {
    textAlign: "center",
    color: "#64748b",
    lineHeight: 1.7,
    fontSize: "1rem",
    marginBottom: theme.spacing(4),
  },
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  sectionTitle: {
    color: "#0f3b62",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  paragraph: {
    color: "#334155",
    lineHeight: 1.9,
    fontSize: "0.98rem",
  },
  list: {
    margin: theme.spacing(1.5, 0, 0, 0),
    paddingLeft: theme.spacing(2.5),
    color: "#334155",
    lineHeight: 1.9,
    fontSize: "0.97rem",
  },
  nestedList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2.5),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  highlight: {
    color: "#2563eb",
  },
}));

export default function MontessoriKeys() {
  const classes = useStyles();

  return (
    <Box className={classes.page}>
      <Paper className={classes.shell}>
        <Typography variant="h4" className={classes.heading}>
          About Key In Montessori
        </Typography>
        <Typography className={classes.intro}>
          A simple guide to common Montessori progress terms and learning materials,
          with cleaner spacing and easier reading on both desktop and mobile.
        </Typography>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Presented</Typography>
          <Typography className={classes.paragraph}>
            In Montessori education, <strong className={classes.highlight}>"Presented"</strong> refers to the structured
            introduction of a new material or concept by the teacher to a child. This presentation guides the child&apos;s
            self-directed learning and helps them understand how to use the material independently.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Works Independently</Typography>
          <Typography className={classes.paragraph}>
            In Montessori education, <strong className={classes.highlight}>"works independently"</strong> signifies a child&apos;s
            ability to engage in learning activities without constant supervision. It supports self-direction, concentration,
            confidence, and deeper understanding through hands-on practice.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Works With Assistance</Typography>
          <Typography className={classes.paragraph}>
            This phrase is often used in progress reports or observation notes to describe a child&apos;s level of independence
            with a specific material or activity.
          </Typography>
          <Typography variant="h6" className={classes.sectionTitle}>What It Means</Typography>
          <ul className={classes.list}>
            <li><strong>Partial Independence:</strong> The child can engage with the work, but still needs verbal prompts, physical guidance, or emotional support.</li>
            <li><strong>Learning in Progress:</strong> The child is practicing and progressing, but has not yet reached full independent mastery.</li>
            <li><strong>Guided Development:</strong> The Montessori guide may support with:
              <ul className={classes.nestedList}>
                <li>Reminders about the sequence of steps</li>
                <li>Help with setup or cleanup</li>
                <li>Modelling correct use of the material</li>
                <li>Encouragement to stay focused</li>
              </ul>
            </li>
          </ul>
          <Typography variant="h6" className={classes.sectionTitle}>Examples</Typography>
          <ul className={classes.list}>
            <li>A child uses the Pink Tower, but the guide reminds them of the correct order.</li>
            <li>A child is learning Sandpaper Letters and needs help tracing or remembering sounds.</li>
          </ul>
          <Typography variant="h6" className={classes.sectionTitle}>Importance</Typography>
          <ul className={classes.list}>
            <li>It shows the child is moving toward independence and mastery.</li>
            <li>It helps educators monitor progress and adjust support levels.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.paragraph}>
            In Montessori education, <strong>Sandpaper Letters</strong> are a key material used to introduce children to the
            alphabet through a <strong>multi-sensory approach</strong>: tactile, visual, and auditory learning.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>What Are Sandpaper Letters?</Typography>
          <Typography className={classes.paragraph}>
            Sandpaper Letters are individual wooden or cardboard cards with lowercase or uppercase letters made of fine
            sandpaper. The child traces the letter with their fingers while saying the matching phonetic sound.
          </Typography>
          <ul className={classes.list}>
            <li><strong>Consonants</strong> are typically on pink or blue cards.</li>
            <li><strong>Vowels</strong> are often on blue or red cards.</li>
            <li><strong>Cursive or print</strong> may be used depending on the school&apos;s Montessori approach.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Purpose of Sandpaper Letters</Typography>
          <ol className={classes.list}>
            <li><strong>Phonemic Awareness:</strong> Children learn sounds, not just letter names.</li>
            <li><strong>Muscle Memory:</strong> Tracing helps build the motor patterns needed for writing.</li>
            <li><strong>Tactile Engagement:</strong> The rough surface supports kinesthetic learning.</li>
            <li><strong>Letter Recognition:</strong> Children connect shapes and sounds together.</li>
          </ol>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>How Are They Used in Practice?</Typography>
          <ol className={classes.list}>
            <li>
              <strong>Three-Period Lesson:</strong>
              <ul className={classes.nestedList}>
                <li><strong>First Period:</strong> “This is <em>m</em>.”</li>
                <li><strong>Second Period:</strong> “Can you show me <em>m</em>?”</li>
                <li><strong>Third Period:</strong> “What is this <em>m</em>?”</li>
              </ul>
            </li>
            <li><strong>Phonetic Sounds First:</strong> Children learn the sound each letter makes before the letter name.</li>
            <li><strong>Traced with Fingers:</strong> Children trace letters from left to right using correct stroke order.</li>
          </ol>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Connection to Reading and Writing</Typography>
          <ul className={classes.list}>
            <li>They prepare children for later writing with a pencil.</li>
            <li>They are often used with the <strong>Movable Alphabet</strong> to start building words.</li>
            <li>Children begin phonetic spelling before formal reading and writing.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Why It&apos;s Effective</Typography>
          <Typography className={classes.paragraph}>
            Maria Montessori designed this method around the sensitive period for language, usually between ages 2.5 and 6.
            The use of touch, movement, and sound supports natural learning during this stage.
          </Typography>
          <Typography className={classes.paragraph}>
            In many Montessori classrooms, Sandpaper Letters are introduced around age 3 to 4, before or alongside the
            child&apos;s first writing attempts.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>The Movable Alphabet</Typography>
          <Typography className={classes.paragraph}>
            The <strong>Movable Alphabet</strong> is a core Montessori language material that helps children build early
            literacy skills. It is usually introduced after a child knows a number of phonetic sounds through the
            Sandpaper Letters.
          </Typography>
          <Typography variant="h6" className={classes.sectionTitle}>What Is It?</Typography>
          <ul className={classes.list}>
            <li>A set of letters, usually lowercase, made of wood or plastic.</li>
            <li>Consonants are often blue and vowels are often red.</li>
            <li>The letters are stored in a compartmentalized box.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Purpose and Educational Goals</Typography>
          <ul className={classes.list}>
            <li>Understand that spoken words can be represented in writing.</li>
            <li>Compose words and later sentences before fluent handwriting develops.</li>
            <li>Practice phonemic awareness by breaking words into sounds.</li>
            <li>Learn spelling patterns and sound-symbol relationships.</li>
            <li>Build a bridge between spoken language and written language.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>How It&apos;s Used</Typography>
          <ol className={classes.list}>
            <li>
              <strong>Word Building:</strong>
              <ul className={classes.nestedList}>
                <li>A child hears a word such as “cat” and selects the letters <strong>c - a - t</strong>.</li>
                <li>They arrange the letters on a mat or table to form the word.</li>
              </ul>
            </li>
            <li><strong>Storytelling:</strong> As skills grow, children can build phrases or stories before they can write fluently.</li>
            <li>
              <strong>Extensions:</strong>
              <ul className={classes.nestedList}>
                <li>Work with pictures and matching words.</li>
                <li>Use color-coded grammar activities.</li>
                <li>Practice phonograms such as <em>sh, th, ch</em>.</li>
              </ul>
            </li>
          </ol>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>Developmental Benefits</Typography>
          <ul className={classes.list}>
            <li>Encourages creativity and confidence in writing.</li>
            <li>Reinforces sound-symbol correspondence.</li>
            <li>Prepares children for writing and reading.</li>
            <li>Supports self-correction and independent exploration.</li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>When It&apos;s Introduced</Typography>
          <ul className={classes.list}>
            <li>Around 3.5 to 4 years old, depending on readiness.</li>
            <li>After the child has a solid foundation in letter sounds from the Sandpaper Letters.</li>
          </ul>
        </Box>
      </Paper>
    </Box>

  );
}
